import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheck, FileX, Lock, Siren, FileCheck2, ScrollText, Building2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import { fetchSummary, CO_STR as S, type ComplianceSummary } from './complianceApi'

function AreaCard({
  icon,
  title,
  to,
  attention,
  stats,
}: {
  icon: React.ReactNode
  title: string
  to: string
  attention: boolean
  stats: { label: string; value: number; danger?: boolean }[]
}) {
  return (
    <Link
      to={to}
      className={`block rounded-lg border p-4 transition-colors hover:border-accent ${attention ? 'border-danger/40 bg-danger/5' : 'border-border-default bg-surface-2'}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-tertiary">{icon}</span>
          <span className="text-sm font-medium text-primary">{title}</span>
        </div>
        <span className={`text-xs ${attention ? 'text-danger' : 'text-success'}`}>
          {attention ? S.attention : S.allClear}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-4">
        {stats.map((st) => (
          <div key={st.label}>
            <p className={`text-xl font-semibold tabular ${st.danger && st.value > 0 ? 'text-danger' : 'text-primary'}`}>{st.value}</p>
            <p className="text-xs text-tertiary">{st.label}</p>
          </div>
        ))}
      </div>
    </Link>
  )
}

export function ComplianceDashboard() {
  const [d, setD] = useState<ComplianceSummary | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchSummary(ac.signal)
      .then(setD)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A18.23"
        description={S.desc}
        actions={
          <button type="button" onClick={() => setTick((t) => t + 1)} className="rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary">
            ↻
          </button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl">
          {err ? (
            <ErrorState code={err.code} onRetry={() => setTick((t) => t + 1)} />
          ) : !d ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-28 w-full" />
              ))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <AreaCard
                icon={<ShieldCheck size={16} />}
                title={S.gdpr}
                to="/gdpr-requests"
                attention={d.gdpr.overdue > 0}
                stats={[
                  { label: S.open, value: d.gdpr.open },
                  { label: S.overdue, value: d.gdpr.overdue, danger: true },
                  { label: S.total, value: d.gdpr.total },
                ]}
              />
              <AreaCard
                icon={<Lock size={16} />}
                title={S.legalHolds}
                to="/legal-holds"
                attention={false}
                stats={[
                  { label: S.active, value: d.legal_holds.active },
                  { label: S.total, value: d.legal_holds.total },
                ]}
              />
              <AreaCard
                icon={<FileX size={16} />}
                title={S.takedowns}
                to="/takedowns"
                attention={d.takedowns.open > 0}
                stats={[
                  { label: S.open, value: d.takedowns.open, danger: true },
                  { label: S.total, value: d.takedowns.total },
                ]}
              />
              <AreaCard
                icon={<Siren size={16} />}
                title={S.emergency}
                to="/emergency-requests"
                attention={d.emergency.critical > 0}
                stats={[
                  { label: S.pending, value: d.emergency.pending },
                  { label: S.critical, value: d.emergency.critical, danger: true },
                  { label: S.total, value: d.emergency.total },
                ]}
              />
              <AreaCard
                icon={<FileCheck2 size={16} />}
                title={S.consents}
                to="/legal-docs"
                attention={false}
                stats={[{ label: S.total, value: d.consents.total }]}
              />
              <AreaCard
                icon={<ScrollText size={16} />}
                title={S.legalDocs}
                to="/legal-docs"
                attention={false}
                stats={[
                  { label: S.published, value: d.legal_docs.published_kinds },
                  { label: S.versions, value: d.legal_docs.versions },
                ]}
              />
              <AreaCard
                icon={<Building2 size={16} />}
                title={S.processors}
                to="/data-processors"
                attention={d.processors.no_dpa > 0}
                stats={[
                  { label: S.active, value: d.processors.active },
                  { label: S.noDpa, value: d.processors.no_dpa, danger: true },
                  { label: S.total, value: d.processors.total },
                ]}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
